import React, { useEffect, useRef, useState } from 'react';
import { AnimatedText } from '../components/AnimatedText';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

interface TeamMember {
    name: string;
    title: string;
    credentials: string;
    bio: string;
    specialty: string;
    linkedIn?: string;
    photo: string;
    category: 'Administrative Team' | 'Clinicians' | 'Interns';
}

export const OurTeamPage: React.FC = () => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const [hoveredMember, setHoveredMember] = useState<number | null>(null);
    const [expandedMember, setExpandedMember] = useState<number | null>(null);
    const [activeCategory, setActiveCategory] = useState<'Administrative Team' | 'Clinicians' | 'Interns'>('Clinicians');

    // All team members combined (no section separation)
    const teamMembers: TeamMember[] = [
        {
            name: 'Gina Rae Garcia',
            title: 'Administrative Support',
            credentials: 'BA',
            bio: 'I am passionate about creating a welcoming, supportive environment where clients feel valued from their very first interaction with our practice. With a background in communication and a person-centered approach, I help foster consistency, emotional safety, and compassionate care throughout the client experience. I believe everyone has a story worth telling and deserves a space where they feel respected, understood, and supported. My role allows me to serve as a bridge between clients and clinicians while helping ensure the therapeutic environment remains organized, professional, and caring. At MSCG, I am honored to contribute to a healing community where every interaction is guided by empathy, integrity, and a commitment to mental wellness.',
            specialty: 'Client Care, Administrative Support, Community Engagement, Client Experience, Emotional Safety',
            photo: '/images/NewHeadshots/Gina Rae Garcia.jpeg',
            category: 'Administrative Team'
        },
        {
            name: 'Ed Bruce',
            title: 'Scheduling Coordinator',
            credentials: 'Virtual Client Concierge & Biller',
            bio: 'Ed serves as the Scheduling Coordinator at MSCG, providing warm and attentive support to every client who reaches out. With several years of experience as a virtual client concierge and biller, he understands the importance of helping individuals find the right clinician for their needs. Ed listens carefully, responds with genuine care, and ensures each inquiry is handled with compassion and clarity. His goal is to make every client feel welcomed, supported, and confident as they take the next step in their wellness journey.',
            specialty: 'Client Support, Scheduling, Intake Coordination',
            photo: '/images/NewHeadshots/Ed Bruce.png',
            category: 'Administrative Team'
        },
        {
            name: 'Michelle Simmons',
            title: 'Founder & Clinical Director',
            credentials: 'LPC',
            bio: 'As the Founder and Clinical Director of Michelle Simmons Counseling Group, I am honored to lead a compassionate team that values healing, growth, and community. For over two decades, I’ve had the privilege of serving individuals and families throughout Colorado, specializing in trauma, crisis intervention, and stress management. My approach is grounded in empathy and empowerment, helping clients find peace, purpose, and renewed confidence. I believe every person deserves a safe, supportive space to explore their emotions and rewrite their story. Outside of work, I enjoy spending time with my family, practicing yoga, and traveling. At MSCG, I strive to create an environment where both clients and clinicians can thrive through authentic connection and shared purpose.',
            specialty: 'Trauma, Mood Disorders, Stress Management, Relationships and Grief.',
            photo: '/images/NewHeadshots/Michelle Simmons Headshot.jpeg',
            category: 'Clinicians'
        },
        {
            name: 'Derrick M. Gildner',
            title: 'Licensed Professional Counselor',
            credentials: 'BS, MA LPC, C-DBT',
            bio: 'My path to becoming a therapist was shaped by a diverse journey through the U.S. Air Force, academia, and mental health care. As a Licensed Professional Counselor with advanced DBT training, I focus on helping clients navigate trauma, stress, and life transitions with compassion and skill. I take pride in creating a grounded, practical therapeutic space where clients can rediscover resilience and balance. My work integrates structure and empathy, drawing from evidence-based interventions to meet each client’s unique needs. When I’m not in session, I enjoy tending to my urban garden, teaching, and spending time with my family and animals. I’m grateful to be part of MSCG’s mission to provide care rooted in integrity, inclusion, and healing.',
            specialty: 'Assessment, Trauma, DBT, Individual and Couples Therapy',
            photo: '/images/Team Members Resized/Team Member_9.png',
            category: 'Clinicians'
        },
        {
            name: 'Emily Palmiotto',
            title: 'Licensed Professional Counselor',
            credentials: 'LPC',
            bio: 'I earned my Master’s Degree in Counseling with an emphasis in couples and families from the University of Colorado. My therapeutic philosophy centers on collaboration, self-discovery, and empowerment. I strive to create a safe, compassionate space where clients feel heard, supported, and understood. I believe therapy is a partnership, and I walk alongside my clients as they explore challenges and move toward meaningful change. My approach blends strength-based, solution-focused, and systemic techniques to meet each person’s unique goals. Outside of work, I enjoy spending time outdoors with my family—hiking, snowboarding, and camping. At MSCG, I’m grateful to be part of a community that values authenticity and transformative care.',
            specialty: 'Anxiety, Stress, Relationship Issues, and Family Dynamics',
            photo: '/images/NewHeadshots/Emily Palmiotto.jpeg',
            category: 'Clinicians'
        },
        {
            name: 'Lynsey M. Chapman',
            title: 'Licensed Professional Counselor',
            credentials: 'MA, LPCC, NCC',
            bio: 'I believe therapy is a collaborative journey where clients are the experts of their own lives. My role is to provide a warm, safe space where healing and growth can unfold naturally. My approach integrates mindfulness, play therapy, and strength-based techniques to support children, adolescents, and adults. I am passionate about helping clients build resilience, self-acceptance, and coping strategies that last beyond our sessions. Clients often describe me as compassionate, authentic, and grounded. In my free time, I enjoy creative expression, music, and time in nature with my dogs. MSCG allows me to practice with integrity and joy, surrounded by a team that genuinely values diversity and well-being.',
            specialty: 'Anxiety, Depression, Trauma, Life Transitions, and Play Therapy',
            photo: '/images/NewHeadshots/Lynsey Chapman Headshot.jpeg',
            category: 'Clinicians'
        },
        {
            name: 'Anika Phason-McCready',
            title: 'Licensed Professional Counselor Candidate',
            credentials: 'MA, LPCC',
            bio: 'I have over two decades of experience helping individuals build healthier relationships and embrace self-acceptance. My approach blends Cognitive Behavioral Therapy, DBT, and Motivational Interviewing, focusing on the “here and now” to foster accountability and growth. I believe in guiding clients toward self-awareness and helping them shift unhelpful patterns into intentional, positive change. I am passionate about supporting people through grief, life transitions, and emotional healing. As a Denver native, I’m proud to serve my community with compassion and understanding. Outside of work, I enjoy connecting with loved ones and finding balance through nature. At MSCG, I’m honored to work alongside a team that values genuine care and collective healing.',
            specialty: 'Trauma, Grief, Anxiety, Depression, and Relationship Issues',
            photo: '/images/NewHeadshots/Anika Phason-McCready.jpeg',
            category: 'Clinicians'
        },
        {
            name: 'Tanisha R. Woodard',
            title: 'Licensed Professional Counselor Candidate',
            credentials: "LPCC",
            bio: 'Life is full of transitions, and sometimes the weight we carry becomes too heavy to bear alone. I help individuals unpack their emotional burdens and rebuild their lives with strength and hope. My therapeutic style is flexible and person-centered—I meet each client where they are, whether that means talking through challenges, taking a reflective walk, or simply holding space for tears. I believe therapy helps us rediscover our purpose and create manageable, fulfilling lives. Outside of work, I enjoy cooking and crafting, both of which bring me joy and balance. At MSCG, I’m inspired by our shared dedication to growth, learning, and community connection.',
            specialty: 'Anxiety, Trauma, Grief, and Depression',
            photo: '/images/NewHeadshots/Tanisha Woodard Headshot.jpeg',
            category: 'Clinicians'
        },
        {
            name: 'Mechelle E. Salley',
            title: 'Unlicensed Psychotherapist',
            credentials: 'MA, ADDC',
            bio: 'I view mental health as a pathway to wholeness—mentally, emotionally, socially, and spiritually. My approach integrates CBT, motivational interviewing, and culturally responsive practices, centering empowerment and self-care as tools for resilience. I work with youth, adults, and leaders navigating burnout, trauma, and mood disorders, especially within BIPOC communities. My sessions blend strategy, humor, and heart to help clients rediscover joy and balance. Outside of therapy, I love reading, dancing, and spending time with loved ones. I’m grateful to work at MSCG, where community, authenticity, and joy are at the core of our healing mission.',
            specialty: 'Trauma, Anxiety, Depression, and Empowerment for Women and Youth',
            photo: '/images/NewHeadshots/Mechelle Salley Headshot.jpeg',
            category: 'Clinicians'
        },
        {
            name: 'Denisha Walker',
            title: 'Marriage & Family Therapy Therapist',
            credentials: 'MFTC, ADDC',
            bio: 'I believe healing begins with self-awareness, compassion, and the courage to rewrite your story. My approach combines person-centered and solution-focused therapy, providing a safe, supportive space for individuals, couples, and families to grow. I draw from both professional training and lived experience as a Christian woman, wife, and mother, integrating empathy, faith, and practical guidance into each session. I enjoy helping clients navigate transitions, strengthen relationships, and find hope through life’s challenges. My work is rooted in the belief that your past does not define your future. At MSCG, I am honored to walk beside clients as they build resilience and embrace change.',
            specialty: 'Couples, Families, Anxiety, Depression, and Relationship Issues',
            photo: '/images/NewHeadshots/Denisha Walker Headshot.png',
            category: 'Clinicians'
        },
        {
            name: 'Javon Bracy',
            title: 'Licensed Professional Counselor Candidate',
            credentials: 'LPCC',
            bio: 'I was born and raised in New Orleans but now consider Denver my second home. My background spans education, nonprofit leadership, and spiritual care, which deeply inform my person-centered approach to therapy. I believe that much of what we need to heal already exists within us—we simply need the right space to uncover it. I see myself as a facilitator, cultivating trust and curiosity as clients explore their thoughts, emotions, and goals. My work focuses on empowerment and helping clients align with their inner wisdom. At MSCG, I’m proud to be part of a team that values compassion, authenticity, and transformation.',
            specialty: 'Empowerment, Life Transitions, and Emotional Wellness',
            photo: '/images/NewHeadshots/Javon Bracy Headshot.jpeg',
            category: 'Clinicians'
        },

        {
            name: 'Jessica Wilson',
            title: 'Licensed Professional Counselor Candidate',
            credentials: "LPCC",
            bio: 'As a trauma-focused therapist, I help clients heal from painful experiences and rediscover safety, self-worth, and strength. My approach integrates CBT, psychodynamic, and mindfulness-based techniques to address the lasting effects of trauma. I’m deeply passionate about supporting women, men, and youth as they navigate healing from abuse, loss, or identity challenges. I believe in creating an environment where clients can process emotions, develop resilience, and move toward empowerment. I’m also an advocate for men’s mental health and youth violence prevention. When I’m not in session, I enjoy traveling, spending time with my family, and watching football with my sons. MSCG’s mission to foster empowerment and inclusivity aligns with my purpose as a therapist.',
            specialty: 'Trauma, Grief, Anxiety, and Relationship Issues',
            photo: '/images/NewHeadshots/Jessica Wilson Headshot.jpeg',
            category: 'Clinicians'
        },
        {
            name: 'Don Thiry',
            title: 'Licensed Addiction Counselor',
            credentials: 'LACC',
            bio: 'I believe that talking about well-being creates well-being. My approach is rooted in collaboration and positivity, helping clients focus on their strengths while addressing areas that could be improved. With over 20 years of sobriety and experience in the Positive Psychology space, I bring authenticity and humor to the therapeutic process. I work primarily with adults navigating substance use and personal growth, guiding them toward sustainable change and fulfillment. Outside of therapy, I enjoy ice cream, blues and punk rock, and time in nature. MSCG’s commitment to diversity and inclusion mirrors my passion for fostering authentic human connection.',
            specialty: 'Substance Use Disorders, Positive Psychology, and Well-Being',
            photo: '/images/NewHeadshots/Don Thiry Headshot.png',
            category: 'Clinicians'
        },
        {
            name: 'Trish Bryant',
            title: 'Student Therapist',
            credentials: 'LAPC',
            bio: 'I am a graduate student at Regis University pursuing my Master of Arts in Counseling with a focus on substance use treatment. My passion lies in transforming pain into purpose and helping clients rediscover strength through healing. Drawing from my cultural identity and personal experience with addiction-affected communities, I offer therapy rooted in compassion, connection, and shared humanity. My goal is to create a safe space where individuals feel seen and empowered to embrace change. I believe that even in struggle, we can find meaning and growth. At MSCG, I’m grateful to learn within a supportive team that reflects the communities we serve.',
            specialty: 'Substance Use, Addiction Recovery, and Personal Empowerment',
            photo: '/images/NewHeadshots/Trish Bryant Headshot.jpeg',
            category: 'Clinicians'
        },
        {
            name: 'Marissa Morton',
            title: 'Graduate Intern 2026',
            credentials: 'Graduate Intern',
            bio: 'I am passionate about helping individuals navigate life\'s challenges with compassion, authenticity, and purpose. My approach is grounded in creating a safe and collaborative space where clients can explore their experiences, build resilience, and work toward meaningful change. I believe healing happens through connection, empathy, and a willingness to honor both where we have been and where we want to go. I work with individuals experiencing trauma, grief, anxiety, depression, self-esteem challenges, and existential concerns, tailoring my approach to each client\'s unique needs and goals. Outside of counseling, I enjoy reading, yoga, basketball, classic soul music, and spending time with my dog, Cerberus. At MSCG, I am grateful to be part of a team committed to making mental health care accessible, inclusive, and empowering for all.',
            specialty: 'PTSD, Grief, Generational Trauma, Depression, Anxiety, Self-Esteem, Existential and Spiritual Concerns',
            photo: '/images/NewHeadshots/Marissa Morton Headshot.png',
            category: 'Interns'
        },
        {
            name: 'Stagia Byrd',
            title: 'Student Intern 2026',
            credentials: 'Student Intern',
            bio: 'I am a Student Intern dedicated to helping individuals discover their strengths and create meaningful, lasting change. My therapeutic philosophy is rooted in empathy, collaboration, and the belief that healing begins when people feel safe, heard, and supported. I strive to create an authentic and nonjudgmental environment where clients can explore their experiences, increase self-awareness, and build resilience. I work with individuals facing anxiety, depression, trauma, grief, relationship challenges, and personal growth goals. Every client\'s journey is unique, and I am committed to meeting people where they are while supporting them in moving toward the life they envision. At MSCG, I value being part of a practice that prioritizes ethical, client-centered care and genuine human connection.',
            specialty: 'Anxiety, Depression, Trauma, Grief, Relationship Concerns, Life Transitions, Goal Setting',
            photo: '/images/placeholder.jpg',
            category: 'Interns'
        }
    ];

    const filteredMembers = teamMembers.filter(member => member.category === activeCategory);

    const toggleExpand = (index: number) => {
        setExpandedMember(expandedMember === index ? null : index);
    };

    // Get currently active member (hovered or expanded)
    const activeIndex = expandedMember !== null ? expandedMember : hoveredMember;

    useEffect(() => {
        gsap.registerPlugin(ScrollTrigger);



        const ctx = gsap.context(() => {
            // Parallax effect for left images - move further left on scroll
            gsap.utils.toArray('.parallax-image-left').forEach((img: any) => {
                const speed = parseFloat(img.dataset.speed) || -0.3;
                gsap.to(img, {
                    x: () => speed * 300,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: '.hero-parallax',
                        start: 'top top',
                        end: 'bottom top',
                        scrub: 1,
                    }
                });
            });

            // Parallax effect for right images - move further right on scroll
            gsap.utils.toArray('.parallax-image-right').forEach((img: any) => {
                const speed = parseFloat(img.dataset.speed) || 0.3;
                gsap.to(img, {
                    x: () => speed * 300,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: '.hero-parallax',
                        start: 'top top',
                        end: 'bottom top',
                        scrub: 1,
                    }
                });
            });

            // Expanding image animation
            gsap.fromTo('.expanding-image-container',
                { width: '70%' },
                {
                    width: '100%',
                    ease: 'none',
                    scrollTrigger: {
                        trigger: '.expanding-image-section',
                        start: 'top 90%', // Start sooner (when section enters viewport)
                        end: 'center center', // End when section is centered
                        scrub: 0.5, // slightly less lag
                        invalidateOnRefresh: true, // Handle resize better
                    }
                }
            );

            // Stagger team rows on scroll
            gsap.from('.team-accordion-row', {
                y: 40,
                opacity: 0,
                duration: 0.6,
                stagger: 0.08,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: '.team-accordion-list',
                    start: 'top 80%',
                }
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <div ref={sectionRef} className="our-team-page bg-white min-h-screen overflow-x-hidden max-w-full">
            {/* Spain Collection-Inspired Hero */}
            <section className="hero-parallax relative min-h-screen bg-[#293039] overflow-hidden">
                {/* Team Member Images - Positioned around edges */}

                {/* Left Side Images */}
                <div
                    className="parallax-image-left absolute left-0 top-[10%] w-[200px] md:w-[280px] lg:w-[320px] z-10 group/hero1 rounded-2xl overflow-hidden hidden md:block"
                    data-speed="-0.3"
                >
                    <img
                        src="/images/Team Members Resized/Still 2025-06-11 174036_1.4.1.png"
                        alt="MSCG Team Members"
                        className="w-full h-auto object-cover shadow-lg transition-all duration-500 group-hover/hero1:-translate-y-2 group-hover/hero1:shadow-2xl"
                    />
                </div>

                <div
                    className="parallax-image-left absolute left-[5%] bottom-[15%] w-[180px] md:w-[240px] lg:w-[280px] z-10 group/hero2 rounded-2xl overflow-hidden hidden md:block"
                    data-speed="-0.5"
                >
                    <img
                        src="/images/Team Members Resized/Emily Palmiotto new.jpeg"
                        alt="Emily Palmiotto"
                        className="w-full h-auto object-cover shadow-lg transition-all duration-500 group-hover/hero2:-translate-y-2 group-hover/hero2:shadow-2xl"
                    />
                </div>

                <div
                    className="parallax-image-left absolute left-[18%] top-[55%] w-[140px] md:w-[180px] lg:w-[200px] z-10 hidden lg:block group/hero3 rounded-2xl overflow-hidden"
                    data-speed="-0.2"
                >
                    <img
                        src="/images/Team Members Resized/Still 2025-06-11 174036_1.11.1.png"
                        alt="MSCG Team Members"
                        className="w-full h-auto object-cover shadow-lg transition-all duration-500 group-hover/hero3:-translate-y-2 group-hover/hero3:shadow-2xl"
                    />
                </div>

                {/* Right Side Images */}
                <div
                    className="parallax-image-right absolute right-0 top-[8%] w-[200px] md:w-[280px] lg:w-[320px] z-10 group/hero4 rounded-2xl overflow-hidden hidden md:block"
                    data-speed="0.3"
                >
                    <img
                        src="/images/Team Members Resized/Still 2025-06-11 174036_1.17.1.png"
                        alt="MSCG Team Members"
                        className="w-full h-auto object-cover shadow-lg transition-all duration-500 group-hover/hero4:-translate-y-2 group-hover/hero4:shadow-2xl"
                    />
                </div>

                <div
                    className="parallax-image-right absolute right-[5%] bottom-[12%] w-[180px] md:w-[240px] lg:w-[280px] z-10 group/hero5 rounded-2xl overflow-hidden hidden md:block"
                    data-speed="0.5"
                >
                    <img
                        src="/images/Team Members Resized/Trish Bryant Headshot new.jpeg"
                        alt="Trish Bryant"
                        className="w-full h-auto object-cover shadow-lg transition-all duration-500 group-hover/hero5:-translate-y-2 group-hover/hero5:shadow-2xl"
                    />
                </div>

                <div
                    className="parallax-image-right absolute right-[20%] top-[65%] w-[140px] md:w-[180px] lg:w-[200px] z-10 hidden lg:block group/hero6 rounded-2xl overflow-hidden"
                    data-speed="0.2"
                >
                    <img
                        src="/images/Team Members Resized/Still 2025-06-11 174036_1.7.1.png"
                        alt="MSCG Team Members"
                        className="w-full h-auto object-cover shadow-lg transition-all duration-500 group-hover/hero6:-translate-y-2 group-hover/hero6:shadow-2xl"
                    />
                </div>

                {/* Center Content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center z-20 px-6">
                    {/* Main Heading - Animated */}
                    <div className="text-center max-w-4xl">
                        <div className="text-4xl md:text-5xl lg:text-6xl xl:text-6xl font-serif text-white mb-8 leading-tight">
                            <AnimatedText
                                text="Our Team"
                                as="span"
                                className="block"
                                delay={300}
                                letterDelay={50}
                                duration={800}
                            />
                        </div>

                        {/* Subtitle */}
                        <p className="text-lg md:text-xl text-white/60 max-w-xl mx-auto mb-10 opacity-0 animate-fade-in-up" style={{ animationDelay: '1.5s', animationFillMode: 'forwards' }}>
                            Meet the dedicated professionals behind MSCG.
                            Learn about our counselors’ credentials, areas of expertise, and what drives our passion to support you.
                        </p>

                        {/* Learn More Button with smooth scroll */}
                        <button
                            onClick={() => {
                                const teamSection = document.getElementById('team-list');
                                if (teamSection) {
                                    teamSection.scrollIntoView({ behavior: 'smooth' });
                                }
                            }}
                            className="inline-flex items-center gap-3 px-8 py-4 bg-mscg-orange text-white font-medium rounded-full hover:bg-white hover:text-mscg-dark-gray transition-all duration-300 opacity-0 animate-fade-in-up group"
                            style={{ animationDelay: '1.8s', animationFillMode: 'forwards' }}
                        >
                            <span>Learn More</span>
                            <svg
                                className="w-5 h-5 transition-transform duration-300 group-hover:translate-y-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                        </button>
                    </div>
                </div>
            </section>

            {/* Expanding Image Section */}
            <section className="expanding-image-section relative py-16">
                <div className="expanding-image-container mx-auto" style={{ width: '70%' }}>
                    <img
                        src="/images/Team Members Resized/Still 2025-06-11 174036_1.5.1.png"
                        alt="MSCG Team"
                        className="w-full h-auto object-cover"
                    />
                </div>
            </section>

            {/* Team Accordion Section */}
            <section id="team-list" className="pb-20 pt-12">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    
                    {/* Tabs */}
                    <div className="flex flex-wrap justify-center gap-4 mb-12">
                        {['Administrative Team', 'Clinicians', 'Interns'].map((cat) => (
                            <button
                                key={cat}
                                onClick={() => {
                                    setActiveCategory(cat as any);
                                    setExpandedMember(null);
                                }}
                                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${activeCategory === cat
                                    ? 'bg-mscg-orange text-white shadow-lg'
                                    : 'bg-neutral-100 text-mscg-dark-gray hover:bg-neutral-200'
                                    }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    <div className="team-accordion-list relative">
                        {filteredMembers.map((member, index) => (
                            <div
                                key={index}
                                className={`team-accordion-row relative border-t-2 border-neutral-400 cursor-pointer transition-colors duration-300 ${hoveredMember === index || expandedMember === index
                                    ? 'bg-[#293039]'
                                    : 'bg-white'
                                    }`}
                                onMouseEnter={() => setHoveredMember(index)}
                                onMouseLeave={() => setHoveredMember(null)}
                                onClick={() => toggleExpand(index)}
                            >
                                {/* Accordion Header Row - Added left padding */}
                                <div className="accordion-heading py-6 md:py-8 pl-6 md:pl-10 pr-4 flex items-center gap-4 md:gap-8">
                                    {/* Name */}
                                    <h3 className={`font-serif text-2xl md:text-3xl lg:text-4xl transition-colors duration-300 whitespace-nowrap ${hoveredMember === index || expandedMember === index
                                        ? 'text-white'
                                        : 'text-mscg-dark-gray'
                                        }`}>
                                        {member.name}
                                    </h3>

                                    {/* Title */}
                                    <p className={`text-xs md:text-sm font-medium hidden md:block transition-colors duration-300 ${hoveredMember === index || expandedMember === index
                                        ? 'text-mscg-orange'
                                        : 'text-mscg-orange'
                                        }`}>
                                        {member.title}
                                    </p>

                                    {/* Spacer - Empty space where image appears */}
                                    <div className="flex-1 relative h-full min-h-[80px] md:min-h-[120px]">
                                        {/* Image appears here on hover - overlays borders with higher z-index */}
                                        <div
                                            className="absolute right-0 top-1/2 -translate-y-1/2 z-50 transition-all duration-500 hidden lg:block"
                                            style={{
                                                opacity: activeIndex === index ? 1 : 0,
                                                transform: `translateY(-50%) ${activeIndex === index ? 'translateX(0)' : 'translateX(20px)'}`,
                                                pointerEvents: 'none',
                                            }}
                                        >
                                            <div className="w-[250px] lg:w-[320px] aspect-[3/4] overflow-hidden shadow-2xl group/img">
                                                <img
                                                    src={member.photo}
                                                    alt={member.name}
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-105"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Large Toggle Icon - Far Right */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleExpand(index);
                                        }}
                                        className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center transition-colors duration-300 flex-shrink-0 ${hoveredMember === index || expandedMember === index
                                            ? 'text-white/60 hover:text-white'
                                            : 'text-neutral-400 hover:text-mscg-dark-gray'
                                            }`}
                                        aria-label={expandedMember === index ? 'Collapse bio' : 'Expand bio'}
                                        aria-expanded={expandedMember === index}
                                    >
                                        <svg
                                            className={`w-8 h-8 md:w-10 md:h-10 transition-transform duration-300 ${expandedMember === index ? 'rotate-45' : ''
                                                }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4v16m8-8H4" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Expandable Bio Content */}
                                <div
                                    className="accordion-content overflow-hidden transition-all duration-500 ease-out"
                                    style={{
                                        maxHeight: expandedMember === index ? '700px' : '0',
                                        opacity: expandedMember === index ? 1 : 0,
                                    }}
                                >
                                    <div className="pb-8 pl-6 md:pl-10 pr-8 md:pr-16 lg:pr-[400px] max-w-4xl">
                                        {/* Mobile Title */}
                                        <p className="text-sm text-mscg-orange font-medium mb-4 md:hidden">
                                            {member.title}
                                        </p>

                                        {/* Credentials */}
                                        <p className="text-sm text-white/50 mb-4 uppercase tracking-wide">
                                            {member.credentials}
                                        </p>

                                        {/* Bio */}
                                        <p className="text-white/80 leading-relaxed mb-6">
                                            {member.bio}
                                        </p>

                                        {/* Specialty Tags with "Expert In:" label */}
                                        <div className="mb-6">
                                            <p className="text-xs text-white/50 uppercase tracking-wider mb-3">Expert In:</p>
                                            <div className="flex flex-wrap gap-2">
                                                {member.specialty.split(', ').map((spec, i) => (
                                                    <span
                                                        key={i}
                                                        className="px-4 py-2 text-xs font-medium bg-[#1a1f25] text-white/90 rounded-full border border-white/10"
                                                    >
                                                        {spec}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Mobile Image */}
                                        <div className="lg:hidden mt-6 mb-4">
                                            <div className="w-full max-w-[300px] aspect-[3/4] overflow-hidden group/mobimg">
                                                <img
                                                    src={member.photo}
                                                    alt={member.name}
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover/mobimg:scale-105"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {/* Bottom border for last item */}
                        <div className="border-t-2 border-neutral-400"></div>
                    </div>
                </div>
            </section>

            {/* Section: Core Values */}
            <section className="values-section py-20 lg:py-24 bg-[#293039]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <p className="text-xs uppercase tracking-[0.3em] text-mscg-orange mb-4">
                            Our Core Values
                        </p>
                        <h3 className="text-3xl lg:text-4xl font-serif text-white">
                            What Guides Our Work
                        </h3>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                        {[
                            { value: 'Compassion', description: 'Meeting you with empathy and understanding' },
                            { value: 'Integrity', description: 'Honest, ethical care you can trust' },
                            { value: 'Respect', description: 'Honoring your dignity and choices' },
                            { value: 'Excellence', description: 'Committed to the highest standards' },
                        ].map((item, index) => (
                            <div key={index} className="text-center">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-mscg-orange/20 flex items-center justify-center">
                                    <span className="text-3xl font-serif text-mscg-orange">{index + 1}</span>
                                </div>
                                <h4 className="text-xl font-serif text-white mb-2">{item.value}</h4>
                                <p className="text-white/60 text-sm">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <style>{`
        .team-accordion-row {
          position: relative;
          z-index: 1;
        }

        /* Ensure hovered/expanded rows are above others but below images */
        .team-accordion-row:hover,
        .team-accordion-row.expanded {
          z-index: 10;
        }

        /* Smooth transitions matching the inspiration */
        .accordion-content {
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
                      opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .accordion-heading {
          transition: padding 0.4s, color 0.4s;
        }

        /* Fade in up animation for hero elements */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out;
        }

        /* Parallax images styling */
        .parallax-image-left,
        .parallax-image-right {
          will-change: transform;
        }

        /* Expanding image section */
        .expanding-image-container {
          will-change: width;
        }
      `}</style>
        </div>
    );
};
